import {textNodes} from "src/common/text_nodes";

/**
 * Highlights text within DOM elements using the CSS Custom Highlight API.
 *
 * @param name - The CSS custom highlight name, used both to register the highlight
 *   and to target it in CSS via `::highlight(name)`. Must be unique per page when
 *   multiple instances are present to avoid collisions.
 */
export class TextHighlighter {
  constructor(private name: string) {}

  /**
   * Highlights all occurrences of each term within the given scope elements.
   * Matching is case-insensitive. Clears any existing highlight before applying new ranges.
   *
   * @param termsToHighlight - The search terms to highlight.
   * @param scope - The elements to search within.
   */
  highlight(termsToHighlight: RegExp[], scope: HTMLElement[]): void {
    if (!CSS.highlights) return;

    const ranges = scope.flatMap((root) =>
      textNodes(root).flatMap((node) =>
        termsToHighlight.flatMap((term) => this.rangesForMatch(node, term)),
      ),
    );

    CSS.highlights.set(this.name, new Highlight(...ranges));
  }

  /** Removes the highlight, restoring all text to its default appearance. */
  clear(): void {
    CSS.highlights?.delete(this.name);
  }

  /** The range covering `term`'s first match in `node`, or none if it doesn't match. */
  private rangesForMatch(node: Text, term: RegExp): Range[] {
    const match = term.exec(node.nodeValue ?? "");
    if (!match) return [];

    const range = document.createRange();
    range.setStart(node, match.index);
    range.setEnd(node, match.index + match[0].length);
    return [range];
  }
}
